import { useLayoutEffect, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import {
  RadioGroup,
  RadioProps,
  ScrollShadow,
  Skeleton,
  Textarea,
  useRadio,
  VisuallyHidden
} from '@heroui/react'
import clsx from 'clsx'
import { usePostHog } from '@posthog/react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import z from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import type {
  PbAvailableReturnReasons,
  PbEcommerceReturnReason,
  ReturnsLineItem
} from '@itsrever/rever-api'
import { useTranslate } from '@/hooks/useTranslate'
import { useAppSelector } from '@/redux/hooks'
import { QuantitySelector } from '@/ui/components/quantity-selector'
import { useItemReturnReasons } from '@/hooks/use-item-return-reasons'
import { isWFPOrder, isWFPReasonCompatible } from '@/utils/worry-free-purchase'
import { FlowPrimaryButton } from './flow-buttons'

// Whether the comment is required depends on the reason selected at submit
// time, so it is checked in the submit handler, not here.
const schema = z.object({
  return_reason: z.string().min(1),
  return_reason_comment: z.string().optional(),
  quantity: z.number()
})

// The mandatory-comment flag lives on each reason; only the selected one decides.
export const isReasonCommentMandatory = (
  reasons: PbAvailableReturnReasons[] | undefined,
  selected: string
): boolean => {
  if (!selected) return false
  for (const group of reasons ?? []) {
    if (group.available_reasons?.length) {
      const sub = group.available_reasons.find((r) => r.name === selected)
      if (sub) return Boolean(sub.mandatory_comment)
    } else if (group.parent_reason?.name === selected) {
      return Boolean(group.parent_reason.mandatory_comment)
    }
  }
  return false
}

export type ReturnReasonData = z.infer<typeof schema>

interface ItemReturnReasonsProps {
  lineItem: ReturnsLineItem
  onContinue: (data: ReturnReasonData) => Promise<void>
}

export const ItemReturnReasons = ({
  onContinue,
  lineItem
}: ItemReturnReasonsProps) => {
  const { t } = useTranslation()
  const { translate } = useTranslate()

  const posthog = usePostHog()

  const checkoutId = useAppSelector(
    (store) => store.returnsApi.order.response?.checkout_id
  )

  const order = useAppSelector(
    (store) => store.returnsApi.order.response?.order
  )

  const itemsToReturn = useAppSelector((store) => store.return.returnedItems)

  const group = useAppSelector(
    (store) => store.settingsApi.settings.response.ecommerce_group
  )

  const isHoff = group === 'hoff'
  const isMisako = group === 'misako'

  const titleClass = clsx('px-4 text-base md:px-5', {
    'font-medium': !isHoff && !isMisako,
    'font-normal': isHoff,
    'font-[300] tracking-wider': isMisako
  })

  const [subReasons, setSubReasons] = useState<PbEcommerceReturnReason[]>([])

  const {
    handleSubmit,
    control,
    register,
    setError,
    formState: { isSubmitting, errors }
  } = useForm<ReturnReasonData>({
    resolver: zodResolver(schema),
    defaultValues: {
      return_reason: '',
      quantity: 1
    }
  })

  const returnReason = useWatch({ control, name: 'return_reason' })

  const ecommerceId = useAppSelector(
    (store) => store.settingsApi.settings.response.ecommerce_id
  )

  const { data, status } = useItemReturnReasons({
    ecommerce_id: ecommerceId,
    category_ids: lineItem.product?.categories?.map(
      (category) => category.id ?? ''
    )
  })

  const commentMandatory = isReasonCommentMandatory(
    data?.return_reasons,
    returnReason
  )

  // WFP does not allow mixing claim and regular return reasons.
  // Once the first item establishes the return type, all other items
  // are restricted to matching reasons. Some claim reasons (e.g.
  // NOT_RECEIVED) further require every item to share that exact
  // reason; the rest may mix freely among claimable reasons.
  const isReasonVisible = (returnReason: string) => {
    if (!isWFPOrder(order?.line_items ?? []) || itemsToReturn.length === 0)
      return true
    return isWFPReasonCompatible(
      returnReason,
      itemsToReturn[0]?.item.product_return_reason
    )
  }

  const visibleReasons =
    data?.return_reasons?.filter(({ parent_reason }) =>
      isReasonVisible(parent_reason!.name!)
    ) ?? []

  const saveReturnReason = async (data: ReturnReasonData) => {
    if (
      commentMandatory &&
      (data.return_reason_comment ?? '').trim().length === 0
    ) {
      setError('return_reason_comment', {
        type: 'custom',
        message: 'return_reason.comment_required'
      })
      return
    }

    posthog?.capture('Selected Return Reason', {
      lineItem,
      return_reason: data.return_reason,
      return_reason_comment: data.return_reason_comment,
      checkout_id: checkoutId
    })

    await onContinue(data)
  }

  if (status === 'error') {
    return (
      <div className="flex flex-1 items-center justify-center p-8 text-sm">
        {t('toast_errors.confirmation_503')}
      </div>
    )
  }

  return (
    <form
      className="flex flex-1 flex-col gap-y-3 overflow-hidden py-4 md:py-5"
      onSubmit={handleSubmit(saveReturnReason)}
    >
      {subReasons.length === 0 ? (
        <>
          <p
            id="return-reasons-options"
            className={titleClass}
            dangerouslySetInnerHTML={{
              __html: translate(`item_selection_return_reasons_title`)
            }}
          />
          <ScrollShadow
            className="h-full flex-1 overflow-y-auto px-2 md:px-3"
            size={80}
          >
            {status === 'pending' ? (
              <div className="flex flex-col gap-0.5">
                {Array.from({ length: 6 }).map((_, i) => {
                  return <Skeleton className="h-11 rounded-md" key={i} />
                })}
              </div>
            ) : null}
            {status === 'success' ? (
              <Controller
                control={control}
                name="return_reason"
                render={({ field: { onChange, value } }) => (
                  <RadioGroup
                    onValueChange={(reason) => {
                      const subReasons = data.return_reasons?.find(
                        ({ parent_reason }) => parent_reason?.name === reason
                      )?.available_reasons

                      if (subReasons && subReasons.length > 0) {
                        if (value) {
                          onChange('')
                        }

                        setSubReasons(subReasons)
                        return
                      }

                      onChange(reason)
                    }}
                    value={value}
                    aria-labelledby="return-reasons-options"
                    orientation="vertical"
                    classNames={{
                      wrapper: 'gap-0.5 flex-nowrap'
                    }}
                  >
                    {visibleReasons.map(({ parent_reason }) => (
                      <ReturnReasonButton
                        value={parent_reason!.name!}
                        key={parent_reason!.name}
                        reason={parent_reason!}
                        isSelected={parent_reason!.name === value}
                      />
                    ))}
                  </RadioGroup>
                )}
              />
            ) : null}
          </ScrollShadow>
        </>
      ) : (
        <>
          <p id="return-reasons-options" className={titleClass}>
            {t('return_reason.select_subreason_title')}
          </p>
          <ScrollShadow
            className="h-full flex-1 overflow-y-auto px-2 md:px-3"
            size={80}
          >
            <Controller
              control={control}
              name="return_reason"
              render={({ field: { onChange, value } }) => (
                <RadioGroup
                  onValueChange={onChange}
                  value={value}
                  aria-labelledby="return-reasons-options"
                  orientation="vertical"
                  classNames={{
                    wrapper: 'gap-0.5 flex-nowrap'
                  }}
                >
                  {subReasons.map((subReason) => {
                    return subReason ? (
                      <ReturnReasonButton
                        value={subReason.name!}
                        key={subReason?.name}
                        reason={subReason}
                        isSelected={subReason.name === value}
                      />
                    ) : null
                  })}
                </RadioGroup>
              )}
            />
          </ScrollShadow>
        </>
      )}
      {returnReason && (
        <div className="mt-auto animate-expand-reason-comment px-4 pt-2 md:px-5">
          <Textarea
            {...register('return_reason_comment')}
            minRows={2}
            maxRows={2}
            isRequired={commentMandatory}
            isInvalid={!!errors.return_reason_comment}
            errorMessage={
              errors.return_reason_comment?.message
                ? t(errors.return_reason_comment.message)
                : undefined
            }
            classNames={{
              inputWrapper: 'rounded-md',
              label: 'truncate font-medium'
            }}
            label={
              <Trans
                i18nKey="return_reason.additional_comment_label"
                values={{
                  return_reason: translate(`select_reason_${returnReason}`)
                }}
              />
            }
          />
        </div>
      )}
      {lineItem.quantity && lineItem.quantity > 1 ? (
        <Controller
          control={control}
          name="quantity"
          render={({ field: { onChange, value } }) => (
            <QuantitySelector
              value={value}
              onValueChange={onChange}
              minValue={1}
              maxValue={lineItem.quantity}
            />
          )}
        />
      ) : null}
      <div className="mt-auto px-4 md:px-5">
        <FlowPrimaryButton
          type="submit"
          isLoading={isSubmitting}
          isDisabled={returnReason.length === 0}
        >
          <span
            dangerouslySetInnerHTML={{
              __html: translate(`item_selection_return_reasons_confirm_button`)
            }}
          />
        </FlowPrimaryButton>
      </div>
    </form>
  )
}

interface ReturnReasonButtonProps extends RadioProps {
  reason: PbEcommerceReturnReason
  isSelected: boolean
}

const ReturnReasonButton = ({
  reason,
  isSelected,
  ...props
}: ReturnReasonButtonProps) => {
  const { translate } = useTranslate()

  const group = useAppSelector(
    (store) => store.settingsApi.settings.response.ecommerce_group
  )

  const isHoff = group === 'hoff'
  const isMisako = group === 'misako'

  const {
    Component,
    getBaseProps,
    getWrapperProps,
    getInputProps,
    getLabelProps,
    getLabelWrapperProps,
    getControlProps
  } = useRadio(props)

  const selectedRef = useRef<HTMLElement>(null)

  useLayoutEffect(() => {
    if (isSelected && selectedRef.current) {
      selectedRef.current.scrollIntoView({
        block: 'nearest'
      })
    }
  }, [isSelected])

  const baseClass = clsx(
    'group flex min-h-11 cursor-pointer items-center py-2.5 data-[selected=true]:scroll-mb-12',
    {
      'rounded-md px-2.5 hover:bg-neutral-50 data-[selected=true]:border-neutral-800 data-[selected=true]:bg-neutral-200/40':
        !isHoff && !isMisako,
      'px-3 md:px-4 border-b border-solid border-black hover:bg-neutral-100 data-[selected=true]:bg-neutral-200/40':
        isHoff,
      'px-3 md:px-4': isMisako
    }
  )

  const labelClass = clsx({
    'line-clamp-2 text-sm font-medium group-data-[selected=true]:font-semibold':
      !isHoff && !isMisako,
    'line-clamp-2 text-sm font-normal': isHoff,
    'text-xs font-[300] uppercase underline-offset-4 group-hover:underline group-data-[selected=true]:underline':
      isMisako
  })

  return (
    <Component {...getBaseProps()} ref={selectedRef} className={baseClass}>
      <VisuallyHidden>
        <input {...getInputProps()} />
      </VisuallyHidden>
      <VisuallyHidden>
        <span {...getWrapperProps()}>
          <span {...getControlProps()} />
        </span>
      </VisuallyHidden>
      <div {...getLabelWrapperProps()} className="px-0">
        <span
          {...getLabelProps()}
          className={labelClass}
          dangerouslySetInnerHTML={{
            __html: translate(`select_reason_${reason.name}`)
          }}
        />
      </div>
    </Component>
  )
}
